import type { AdminFailedFlow } from "../../contracts/admin-read-models";
import { formatAdminCount } from "../formatting/format-admin-count";
import type { AdminDashboardLocale } from "./admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "./admin-dashboard-copy";

export type AdminFailedFlowRowHotspotItemViewModel = {
  id: "documentPipeline" | "authProvider" | "fileCleanup";
  label: string;
  value: string;
};

export type AdminFailedFlowRowHotspotViewModel = {
  title: string;
  sourceGapLabel: string;
  items: AdminFailedFlowRowHotspotItemViewModel[];
};

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return locale === "ko" ? ko : en;
}

function formatRowCount(count: number, locale: AdminDashboardLocale): string {
  return locale === "ko"
    ? `${formatAdminCount(count)}건`
    : `${count} row${count === 1 ? "" : "s"}`;
}

function joinHotspotValue(
  count: number,
  parts: string[],
  locale: AdminDashboardLocale,
): string {
  const visibleParts = parts.filter((part) => part.length > 0);
  if (visibleParts.length === 0) {
    return formatRowCount(count, locale);
  }

  return [formatRowCount(count, locale), ...visibleParts].join(" / ");
}

function buildDocumentPipelineItem(
  rows: AdminFailedFlow[],
  locale: AdminDashboardLocale,
): AdminFailedFlowRowHotspotItemViewModel | undefined {
  const healthCount = rows.filter(
    (row) => row.signalKey === "health_record" && row.stage === "health_analysis_process",
  ).length;
  const receiptCount = rows.filter(
    (row) => row.signalKey === "receipt_record" && row.stage === "receipt_extract",
  ).length;
  const dailyCount = rows.filter(
    (row) => row.signalKey === "daily_record" && row.stage === "daily_draft_create",
  ).length;
  const total = healthCount + receiptCount + dailyCount;

  if (total === 0) {
    return undefined;
  }

  return {
    id: "documentPipeline",
    label: text(locale, "Document pipeline", "문서 처리 파이프라인"),
    value: joinHotspotValue(
      total,
      [
        healthCount > 0 ? text(locale, `Health ${healthCount}`, `건강 ${healthCount}`) : "",
        receiptCount > 0 ? text(locale, `Receipt ${receiptCount}`, `영수증 ${receiptCount}`) : "",
        dailyCount > 0 ? text(locale, `Daily ${dailyCount}`, `일상 ${dailyCount}`) : "",
      ],
      locale,
    ),
  };
}

function buildAuthProviderItem(
  rows: AdminFailedFlow[],
  locale: AdminDashboardLocale,
): AdminFailedFlowRowHotspotItemViewModel | undefined {
  const linkCount = rows.filter(
    (row) => row.signalKey === "auth_session" && row.stage === "auth_provider_link",
  ).length;
  const unlinkCount = rows.filter(
    (row) => row.signalKey === "auth_session" && row.stage === "auth_provider_unlink",
  ).length;
  const total = linkCount + unlinkCount;

  if (total === 0) {
    return undefined;
  }

  return {
    id: "authProvider",
    label: text(locale, "Auth provider", "인증 provider"),
    value: joinHotspotValue(
      total,
      [
        linkCount > 0 ? text(locale, `Link ${linkCount}`, `연결 ${linkCount}`) : "",
        unlinkCount > 0 ? text(locale, `Unlink ${unlinkCount}`, `해제 ${unlinkCount}`) : "",
      ],
      locale,
    ),
  };
}

function buildFileCleanupItem(
  rows: AdminFailedFlow[],
  locale: AdminDashboardLocale,
): AdminFailedFlowRowHotspotItemViewModel | undefined {
  const cleanupRows = rows.filter(
    (row) => row.signalKey === "file_lifecycle" && row.stage === "file_cleanup",
  );

  if (cleanupRows.length === 0) {
    return undefined;
  }

  const topErrorCode = cleanupRows.reduce<{ errorCode?: string; count: number }>(
    (currentBest, row) => {
      const rowCount = cleanupRows.filter((candidate) => candidate.errorCode === row.errorCode).length;
      if (rowCount > currentBest.count) {
        return { errorCode: row.errorCode, count: rowCount };
      }

      if (rowCount === currentBest.count && currentBest.errorCode && row.errorCode < currentBest.errorCode) {
        return { errorCode: row.errorCode, count: rowCount };
      }

      return currentBest;
    },
    { count: 0 },
  ).errorCode;

  return {
    id: "fileCleanup",
    label: text(locale, "File cleanup", "파일 정리"),
    value: joinHotspotValue(
      cleanupRows.length,
      [topErrorCode ?? text(locale, "Rows present", "행 존재")],
      locale,
    ),
  };
}

export function buildAdminFailedFlowRowHotspotViewModel(
  rows: AdminFailedFlow[],
  locale: AdminDashboardLocale,
): AdminFailedFlowRowHotspotViewModel | undefined {
  const items = [
    buildDocumentPipelineItem(rows, locale),
    buildAuthProviderItem(rows, locale),
    buildFileCleanupItem(rows, locale),
  ].flatMap((item) => (item ? [item] : []));

  if (items.length === 0) {
    return undefined;
  }

  return {
    title: pickAdminDashboardLocaleText(locale, {
      en: "Current row hotspots",
      ko: "현재 행 hotspot",
    }),
    sourceGapLabel: pickAdminDashboardLocaleText(locale, {
      en: "Derived from the failed-flow rows already visible on this screen. Use it to choose the next filter or drilldown, not as proof that upstream aggregate observability summaries are populated.",
      ko: "이 화면에 이미 보이는 failed-flow rows만으로 다시 계산한 snapshot입니다. 다음 필터나 drilldown을 고르는 데 사용하고, upstream 관측 집계 source가 채워졌다는 증거로 읽지 마세요.",
    }),
    items,
  };
}
